import {useState, useCallback, useEffect} from 'react';

import {useDispatch} from 'react-redux';

import AutoCreditView, {AutoCreditViewProps} from '~/shared/components/AutoCreditView';
import {setCurrentModal} from '~/shared/store/actions';
import {createLogger} from '~/shared/logging';
import {updateAutoCreditSubscription} from '~/shared/services/bffService/api';
import {trackEvent} from '~/shared/services/analytics';
import {getLocalizationService} from '~/shared/services/localisationService';
import {is401Error} from '~/shared/services/apiErrorService';
import {handleRefreshToken} from '~/shared/services/auth';

const logger = createLogger('AutoCredit');

interface AutoCreditProps extends Pick<AutoCreditViewProps, 'withShadow' | 'variant' | 'backgroundColor' | 'className' | 'isActive'> {
  moneycardId: string;
  encryptedMoneycardId: string;
  onSuccess?: () => void;
}

const AutoCredit = (props: AutoCreditProps) => {
  const {moneycardId, encryptedMoneycardId, isActive, onSuccess, ...restProps} = props;
  const dispatch = useDispatch();
  const {t} = getLocalizationService();

  const [isButtonActive, setIsButtonActive] = useState(isActive);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    setIsButtonActive(isActive);
  }, [isActive]);
  
  const onInfoButtonClick = () => {
    dispatch(setCurrentModal('auto_credit_info_modal'));
  };

  const handleToggleClick = useCallback(async (newActiveState: boolean) => {
    if (isLoading) {
      return;
    }
    setIsLoading(true);
    setIsButtonActive(newActiveState);
    setIsError(false);
  
    const updateAutoCredit = async () => {
      await updateAutoCreditSubscription({
        encryptedMoneycardId,
        autoCreditSubscribed: newActiveState,
      });
      setIsLoading(false);
    
      trackEvent('autoCreditToggle', {
        toggleOption: newActiveState,
      });
    
      onSuccess?.();
    };

    try {
      await updateAutoCredit();
    } catch (error) {
      if (is401Error(error)) {
        handleRefreshToken(error, updateAutoCredit);
        return;
      }

      logger.error('Failed to update auto credit subscription', {
        error,
        moneycardId,
        attemptedState: newActiveState,
      });
      setIsButtonActive(!newActiveState);

      trackEvent('autoCreditToggleFailed', {
        toggleOption: newActiveState,
        apiError: (error as Error)?.message || 'Unknown error occurred',
        errorMessage: t('something_went_wrong_try_again_later'),
      });

      setIsError(true);
      setIsLoading(false);
    }
  }, [encryptedMoneycardId, isLoading, moneycardId, onSuccess, t]);
  return <AutoCreditView {...restProps} isActive={isButtonActive} isError={isError} onInfoButtonClick={onInfoButtonClick} handleToggleClick={handleToggleClick}/>;
};

export default AutoCredit;
